import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const StudentFees = () => {
  const [fees, setFees] = useState([]);
  const [stats, setStats] = useState({
    total_due: 0,
    total_paid: 0,
    total_pending: 0,
    remaining: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFees();
  }, []);

  const fetchFees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/student/fees', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFees(Array.isArray(res.data.fees) ? res.data.fees : []);
      setStats({
        total_due: parseFloat(res.data.total_due) || 0,
        total_paid: parseFloat(res.data.total_paid) || 0,
        total_pending: parseFloat(res.data.total_pending) || 0,
        remaining: parseFloat(res.data.remaining) || 0,
      });
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getProgressPercent = () => {
    if (stats.total_due === 0) return 0;
    return Math.min(100, Math.round((stats.total_paid / stats.total_due) * 100));
  };

  if (loading) return <div className="p-6">Chargement...</div>;

  const progressPercent = getProgressPercent();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">💰 Mes frais scolaires</h1>
        <p className="text-gray-500">Consultez votre situation financière</p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Pour effectuer un paiement, veuillez contacter votre parent ou tuteur.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-gray-700">
          <p className="text-sm text-gray-500 mb-1">📊 Total dû</p>
          <p className="text-2xl font-bold text-gray-800">{formatAmount(stats.total_due)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
          <p className="text-sm text-gray-500 mb-1">✅ Déjà payé</p>
          <p className="text-2xl font-bold text-green-600">{formatAmount(stats.total_paid)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
          <p className="text-sm text-gray-500 mb-1">⏳ Reste à payer</p>
          <p className="text-2xl font-bold text-orange-600">{formatAmount(stats.remaining)}</p>
        </div>
      </div>

      {/* Barre de progression */}
      {stats.total_due > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium text-gray-700">Progression du paiement</p>
            <p className="text-sm font-bold text-[#E67E22]">{progressPercent}%</p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-gradient-to-r from-[#E67E22] to-[#D35400] h-4 rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {formatAmount(stats.total_paid)} payé sur {formatAmount(stats.total_due)}
          </p>
        </div>
      )}

      {/* Liste des frais */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-[#2C3E50]">
            📋 Détail des frais ({fees.length})
          </h3>
        </div>
        {fees.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg mb-2">📭 Aucun frais enregistré</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Type de frais</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Montant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Échéance</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                {fees.map((fee) => (
                  <tr key={fee.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{fee.fee_type}</td>
                    <td className="py-3 px-4 font-bold text-[#2C3E50]">{formatAmount(fee.amount)}</td>
                    <td className="py-3 px-4 text-sm">{fee.due_date}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{fee.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentFees;